import * as XLSX from 'xlsx';
import { update as coreUpdate } from './workbookInternals';

// Same values `isSafeNumFmt` probes against both engines: a representative
// positive number, negative number, zero, and a non-numeric string (formats
// that only work for numbers, e.g. General text placeholders, still need to
// not throw on a string — that's what a text cell would pass through).
const PROBE_VALUES: (number | string)[] = [1234.5, -1234.5, 0, 'text'];

/**
 * A format string is only "safe" to apply if BOTH the core SSF (`update`,
 * used by the live grid) and SheetJS's SSF (`XLSX.SSF.format`, used on
 * download/re-import) accept it for every probe value, and it's 1-255 chars
 * (Excel's own custom-format length cap). Anything that fails either engine
 * would either crash the reducer (core, unguarded — RCPI-10925) or silently
 * fail to round-trip through Download/re-import (SheetJS).
 */
export const isSafeNumFmt = (fa: string): boolean => {
    if (fa.length < 1 || fa.length > 255) return false;
    const update = coreUpdate;
    if (!update) return false;
    return PROBE_VALUES.every((probe) => {
        try {
            update(fa, probe);
        } catch {
            return false;
        }
        try {
            XLSX.SSF.format(fa, probe);
        } catch {
            return false;
        }
        return true;
    });
};

// The app's fixed currency symbol (no multi-currency support) — used by
// FormatCellsDialog, the Ctrl+Shift+$ preset and the Workbook `currency` prop.
export const RUPEE_SYMBOL = '₹';

export type NumFmtCategory = 'number' | 'currency' | 'percentage' | 'scientific' | 'text';
export type NumFmtNegativeStyle = 'minus' | 'red' | 'paren' | 'red-paren';

export interface BuildNumFmtOptions {
    decimals?: number;
    thousands?: boolean;
    negative?: NumFmtNegativeStyle;
    /** Currency symbol/prefix — defaults to the app's fixed INR symbol (no
     *  multi-currency support, matching <Workbook currency="₹">). */
    currency?: string;
}

const decimalPart = (decimals: number): string => (decimals > 0 ? `.${'0'.repeat(decimals)}` : '');

/** Builds an SSF format string from FormatCellsDialog's structured
 *  options — the one place format strings are assembled, so every caller
 *  (the dialog's live preview and its Apply) stays consistent. */
export const buildNumFmt = (category: NumFmtCategory, opts: BuildNumFmtOptions = {}): string => {
    if (category === 'text') return '@';
    if (category === 'scientific') return `0${decimalPart(opts.decimals ?? 2)}E+00`;
    if (category === 'percentage') return `0${decimalPart(opts.decimals ?? 2)}%`;

    const decimals = opts.decimals ?? 2;
    // Currency defaults to grouped digits even when unspecified; plain
    // Number only groups when explicitly asked.
    const thousands = opts.thousands ?? category === 'currency';
    const grouped = thousands ? '#,##0' : '0';
    // Quoted as a literal — SheetJS's SSF rejects a bare non-ASCII currency
    // character (isSafeNumFmt's second engine), even though core's own SSF
    // accepts it unquoted. Quoting renders identically in both.
    const prefix = category === 'currency' ? `"${opts.currency ?? RUPEE_SYMBOL}"` : '';
    const positive = `${prefix}${grouped}${decimalPart(decimals)}`;

    switch (opts.negative) {
        case 'red':
            return `${positive};[Red]-${positive}`;
        case 'paren':
            return `${positive};(${positive})`;
        case 'red-paren':
            return `${positive};[Red](${positive})`;
        default:
            return positive;
    }
};

export interface DefaultFmtEntry {
    text: string;
    value: string;
    example: string;
    icon?: string;
}
export type DefaultFmtFn = (currency: string) => DefaultFmtEntry[];

/**
 * The library's own `defaultFmt(currency)` Number
 * preset is `##0.00` (no thousands separator) and its Percent preset is the
 * non-standard `#0.00%`. These are the corrected values `wrapDefaultFmt`
 * substitutes in, keyed by the original entry's `text` label so every other
 * entry (Automatic, Plain text, Date/Time, Custom formats, …) passes through
 * untouched.
 */
export const EXCEL_NUMBER_PRESETS = (currency: string): Record<string, { value: string; example: string }> => ({
    Number: { value: buildNumFmt('number', { decimals: 2, thousands: true }), example: '1,000.12' },
    Percent: { value: buildNumFmt('percentage', { decimals: 2 }), example: '12.21%' },
    Currency: { value: buildNumFmt('currency', { decimals: 2, currency }), example: `${currency}1,200.09` },
    Accounting: {
        value: `_("${currency}"* #,##0.00_);_("${currency}"* (#,##0.00);_("${currency}"* "-"??_);_(@_)`,
        example: `${currency}1,200.09`,
    },
    Scientific: { value: buildNumFmt('scientific', { decimals: 2 }), example: '1.01E+3' },
});

/** Strips an Excel/SSF number-format string down to its live format codes —
 *  drops quoted literal text (`"..."`) and backslash-escaped characters
 *  (`\x`) — so a literal unit suffix (e.g. `0.00" yd"`, "yards") can't be
 *  mistaken for date tokens just because the letters `y`/`d` appear inside it.
 *
 *  `XLSX.SSF.is_date` (already in this bundle, used above for isSafeNumFmt)
 *  was considered in place of this hand-rolled parser, but it also classifies
 *  a pure TIME format (e.g. `h:mm AM/PM`) as a date — verified empirically —
 *  which isDateCell below must not. */
export const stripFormatLiterals = (fa: string): string => {
    let live = '';
    let inQuotes = false;
    for (let i = 0; i < fa.length; i += 1) {
        const ch = fa[i];
        if (inQuotes) {
            if (ch === '"') inQuotes = false;
            continue;
        }
        if (ch === '"') {
            inQuotes = true;
        } else if (ch === '\\') {
            i += 1; // the escaped character itself is also literal — skip it too
        } else {
            live += ch;
        }
    }
    return live;
};

/** True when `fa`'s LIVE format codes (literals and bracketed sections
 *  stripped, see above) contain a real day-of-month or year token — `d`/`dd`/`ddd`/`dddd` or `yy`/`yyyy`.
 *  `m`/`mm`/`mmm` alone is deliberately not a trigger on its own: unlike d/y,
 *  `m` is ambiguous with minutes in a pure time format (e.g. `h:mm AM/PM`),
 *  and every real date format in this app already pairs it with a d or y
 *  token anyway (`d-mmm-yy`, `yyyy-MM-dd`, …). */
export const hasDateFormatToken = (fa: string): boolean =>
    // Bracketed sections ([Red], [Color10], [$-409], [h]) are colours/locales/
    // elapsed-time codes, never day or year tokens.
    Array.from(
        stripFormatLiterals(fa)
            .replace(/\[[^\]]*\]/g, '')
            .matchAll(/([dy])\1*/gi),
    ).some(([run, letter]) => (letter.toLowerCase() === 'd' ? run.length <= 4 : run.length === 2 || run.length === 4));

/** True when `fa`'s live format codes carry an hour token (`h`/`H`) —
 *  shared by both a pure time format and a date+time format. */
export const hasTimeFormatToken = (fa: string): boolean =>
    // Colour/locale brackets ([White], [$CHF]) are not time tokens; elapsed-time
    // brackets ([h], [hh], [mm], [ss]) are kept.
    /h/i.test(stripFormatLiterals(fa).replace(/\[(?![hms]+\])[^\]]*\]/gi, ''));

/** True for a date-formatted cell: the library's own typed-date classifier
 *  (`ct.t === 'd'`, set when a date is typed directly into a cell) and this
 *  app's own xlsx/csv import + number-format presets (dates import as
 *  `ct.t === 'n'` with a date-shaped `fa` — see converters.ts's own
 *  "dates import as numeric serials, ct.t === 'n'" comment) both occur in
 *  practice, so either signal counts. */
export const isDateCell = (cell: { ct?: { t?: string; fa?: string } } | null | undefined): boolean => {
    const fa = cell?.ct?.fa;
    return cell?.ct?.t === 'd' || (typeof fa === 'string' && hasDateFormatToken(fa));
};

/** Wraps the library's `defaultFmt(currency)` function so every call returns
 *  the corrected presets — the original is called exactly once per invocation
 *  and never mutated, so wrapping twice never double-applies. */
export const wrapDefaultFmt = (original: DefaultFmtFn): DefaultFmtFn => {
    return (currency: string): DefaultFmtEntry[] => {
        const overrides = EXCEL_NUMBER_PRESETS(currency);
        return original(currency).map((entry) => {
            const override = overrides[entry.text];
            return override ? { ...entry, ...override } : entry;
        });
    };
};
